import cs from 'classnames';

import {
  allowAsciiOnlyChars,
  allowValidNameChars,
  ia,
  validateNonASCII
} from 'lib/helpers/utility';

/**
 * Get common props for Input component
 * @param {import('./propTypes').InputProps} props
 */

export const getCommonInputProps = ({
  // Layout props
  className,
  width,
  inputClassName,
  inputWrapperClassName,
  rounded,

  // Input props
  label,
  name = label || 'inputName',
  id = name || label || 'inputId',
  type = 'text',
  value,
  placeholder,
  disabled = false,
  autoComplete = 'off',

  // Number input controls
  hideNumberArrows,
  hideNumberControls = false,

  // Icons and actions
  icon,
  iconSize = 20,
  rightIcon,
  rightIconColor,
  iconClick,
  rightIconClick,
  rightSecondaryIcon,
  rightIconSize = 20,
  actionButtonText,
  actionButtonIcon,
  units,

  // Form behavior
  formik,
  focusNextFieldOnEnter = false,
  submitFormOnEnter,
  validateName = false,

  // Misc
  forwardedRef,
  onChange = () => {},

  //validation for non ascii characters
  asciiOnly = false,
  activeError = null,
  setLocalError = () => {},
  ...rest
}) => {
  const roundedCss = rounded ? `rounded-${rounded}` : 'rounded-md';
  const hasUnitOptions = ia(units);
  const hasActionButton = actionButtonText || actionButtonIcon || hasUnitOptions;

  const isNumberType = type === 'number';
  const enableCustomNumberControls = isNumberType && !hideNumberControls;
  const showNumberControls = enableCustomNumberControls && !icon && !rightIcon;

  const handleInternalNumberChange = (action) => {
    if (!isNumberType) return;
    const currentValue = Number(value) || 0;
    const newValue = action === 'increment' ? currentValue + 1 : Math.max(0, currentValue - 1);

    // Original event structure
    const syntheticEvent = { target: { value: newValue } };

    onChange(syntheticEvent);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !submitFormOnEnter) {
      event.preventDefault();
    }

    if (event.key === 'Enter' && focusNextFieldOnEnter && formik) {
      const fields = Object.keys(formik.values);
      const index = fields.indexOf(name);
      if (index >= 0) {
        const nextFieldIndex = index + 1;
        if (nextFieldIndex < fields.length) {
          document.querySelector(`input[name="${fields[nextFieldIndex]}"]`)?.focus();
        } else {
          formik.submitForm();
        }
      }
    }
  };

  const handleKeyDown = (event) => {
    if (asciiOnly) return allowAsciiOnlyChars(event, setLocalError);
    if (validateName) return allowValidNameChars(event);
    return;
  };

  const handlePaste = (event) => {
    if (asciiOnly) return allowAsciiOnlyChars(event, setLocalError);
    return;
  };

  const handleChange = (event) => {
    const value = event?.target?.value;

    if (asciiOnly && validateNonASCII(value)) {
      setLocalError('Please use regular letters and numbers only.');
      return;
    }
    setLocalError(null);
    onChange(event);
  };

  return {
    className,
    style: { width },
    inputProps: {
      className: cs(
        'w-full !opacity-100 overflow-auto text-ellipsis border-none bg-transparent p-0 text-sm leading-5 text-neutral-900 outline-none placeholder:text-neutral-500',
        activeError ? 'caret-danger-500' : 'caret-primary-500',
        disabled && 'cursor-not-allowed !text-neutral-500',
        (hideNumberArrows || isNumberType) &&
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        enableCustomNumberControls && 'text-center select-none',
        inputClassName
      ),
      value,
      onChange: handleChange,
      onKeyPress: handleKeyPress,
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
      placeholder,
      id,
      'data-qa': id,
      name,
      type,
      disabled,
      ref: forwardedRef,
      autoComplete,
      ...rest
    },
    wrapperProps: {
      className: cs(
        'h-10 m-0 select-none flex items-center justify-between gap-x-3 border border-solid bg-white focus-within:outline focus-within:!outline-2',
        activeError
          ? '!border-danger-300 focus-within:!outline-danger-100'
          : 'border-neutral-200 focus-within:border-primary-300 focus-within:outline-[#CBF0FD]',
        roundedCss,
        disabled && '!bg-neutral-50',
        !hasActionButton ? 'px-3 py-[10px]' : 'overflow-hidden pl-3',
        inputWrapperClassName
      )
    },
    iconProps: {
      iconSize,
      leftIcon: enableCustomNumberControls ? icon || 'minus-linear' : icon,
      rightIcon: enableCustomNumberControls ? rightIcon || 'add-linear' : rightIcon,
      rightIconColor,
      leftIconClick: enableCustomNumberControls
        ? () => handleInternalNumberChange('decrement')
        : iconClick,
      rightIconClick: enableCustomNumberControls
        ? () => handleInternalNumberChange('increment')
        : rightIconClick,
      rightSecondaryIcon,
      rightIconSize
    },
    showNumberControls
  };
};
